import { FramePanel } from "@/components/ui/frame";
import { FunnelSummary, Metric } from "./FunnelSummary";
import { CREATOR_STATUS_ORDER, fmtNum } from "./format";
import type { BatchStatus, RawCreatorStatus } from "./types";

const EMPTY_COUNTS: Record<RawCreatorStatus, number> = {
  PENDING: 0,
  ACCEPTED: 0,
  REJECTED: 0,
  ERRORED: 0,
  DUPLICATE: 0,
};

/**
 * Totals across every batch on the page — each batch's own numbers are in
 * its card. A creator belongs to exactly one batch (the one that inserted
 * it), so the sums don't double count.
 */
export function BatchesOverview({ batches }: { batches: BatchStatus[] }) {
  const totals = { ...EMPTY_COUNTS };
  let creators = 0;
  let keywordsDone = 0;
  let keywordsTotal = 0;
  let failed = 0;
  for (const batch of batches) {
    creators += batch.total;
    for (const status of CREATOR_STATUS_ORDER) totals[status] += batch.status_counts[status] ?? 0;
    const { source, jobs, fan_out } = batch.sourcing;
    if (source !== "source_jobs") continue;
    keywordsDone += jobs.filter((j) => j.status === "done").length;
    keywordsTotal += fan_out?.keywords?.length ?? jobs.length;
    failed += jobs.filter((j) => j.status === "failed").length + (fan_out?.status === "failed" ? 1 : 0);
  }

  return (
    <FramePanel className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 className="text-sm font-medium">All batches</h3>
        <span className="text-xs text-muted-foreground">{batches.length} batches · counted in the batch that inserted each creator</span>
      </div>
      <FunnelSummary
        counts={totals}
        total={creators}
        extra={
          keywordsTotal > 0 ? (
            <Metric label="Keyword runs" value={`${fmtNum(keywordsDone)}/${fmtNum(keywordsTotal)}`}>
              {failed > 0 && <span className="text-xs text-destructive">{failed} failed</span>}
            </Metric>
          ) : undefined
        }
      />
    </FramePanel>
  );
}
